import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuthStore } from '@/auth/auth-store';

/**
 * "Timezone difference" modal (PROD-1026, F-AUT-046) — shown when login
 * detects the device timezone differs from the user's saved timezone (the
 * auth-store sets `timezoneMismatch` instead of completing login). On OK we
 * clear local data + re-login; the saved tz was already updated server-side,
 * so the fresh login matches and completes.
 *
 * Mirrors Flutter `showTimezoneDifferencePopup` (`session_helper.dart:256-267`)
 * driven from `login_screen.dart:128-138`:
 *   - Title: "Timezone difference"  (l10n `timezoneDifference`)
 *   - Body:  "Hello {firstName} your current timezone doesn’t match your saved
 *            timezone, Ok to relogin."  (l10n `yourCurrentTimezoneDoesntMatch…`)
 *   - Single CTA "Ok" (`hideNo: true`) → clearLocalData + re-fire login.
 *
 * Mounted once at app root. OK-only and non-dismissable via outside-click /
 * escape — matches Flutter `confirmDialog` barrier-block. The `firstName` is
 * omitted gracefully when absent (avoids a literal "Hello null").
 */
export function TimezoneMismatchDialog() {
  const timezoneMismatch = useAuthStore((s) => s.timezoneMismatch);
  const confirmTimezoneRelogin = useAuthStore((s) => s.confirmTimezoneRelogin);

  // In-flight covers the clear + re-login round trip; disables the button so
  // a double-click can't double-submit.
  const [isInFlight, setIsInFlight] = useState(false);

  const open = timezoneMismatch !== null;
  const firstName = timezoneMismatch?.firstName;

  async function onConfirm() {
    if (isInFlight) return;
    setIsInFlight(true);
    try {
      await confirmTimezoneRelogin();
    } finally {
      setIsInFlight(false);
    }
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <AlertDialogHeader>
          <AlertDialogTitle>Timezone difference</AlertDialogTitle>
          <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
            {`Hello ${firstName ? `${firstName} ` : ''}your current timezone doesn’t match your saved timezone, Ok to relogin.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={(e) => {
              // Prevent base-ui's default close — confirmTimezoneRelogin
              // drives the state flip (clear + re-login).
              e.preventDefault();
              void onConfirm();
            }}
            disabled={isInFlight}
          >
            {isInFlight ? 'Relogging in…' : 'Ok'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default TimezoneMismatchDialog;
